import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import SupportCoursesService from "services/SupportCoursesService";

// Thunks
export const getSupportCourses = createAsyncThunk(
  "supportCourses/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SupportCoursesService.getSupportCourses();
      return response.data.supportCourses;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const getSupportCourse = createAsyncThunk(
  "supportCourses/getOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await SupportCoursesService.getSupportCourse(id);
      console.log("response", response.data.supportCourse);
      return response.data.supportCourse;
    } catch (error) {
      console.error("Error fetching support course:", error);
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const createSupportCourse = createAsyncThunk(
  "supportCourses/create",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await SupportCoursesService.createSupportCourse(
        formData
      );
      return response.data.supportCourse;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const updateSupportCourse = createAsyncThunk(
  "supportCourses/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await SupportCoursesService.updateSupportCourse(
        id,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const deleteSupportCourse = createAsyncThunk(
  "supportCourses/delete",
  async (id, { rejectWithValue }) => {
    try {
      await SupportCoursesService.deleteSupportCourse(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const addStudents = createAsyncThunk(
  "supportCourses/addStudents",
  async ({ id, students }, { rejectWithValue }) => {
    try {
      const response = await SupportCoursesService.addStudents(id, {
        students,
      });
      return response.data;
    } catch (error) {
      console.error("Error adding students to support course:", error);
      return rejectWithValue(error.response?.data.message || "Error");
    }
  }
);

export const deleteStudent = createAsyncThunk(
  "supportCourses/deleteStudent",
  async ({ id, studentSupportCourseId }, { rejectWithValue }) => {
    try {
      await SupportCoursesService.deleteStudent(id, { studentSupportCourseId });
      return { id, studentSupportCourseId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error");
    }
  }
);

export const getAvailableStudentSupportCourses = createAsyncThunk(
  "supportCourses/getAvailableStudentSupportCourses",
  async ({ supportCourseId, groupId }, { rejectWithValue }) => {
    try {
      const response =
        await SupportCoursesService.getStudentSupportCourseForSupportCourse(
          supportCourseId,
          groupId
        );
      return response.data.studentSupportCourses;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error");
    }
  }
);

// Thunk pour valider un cours de soutien
export const validateSupportCourse = createAsyncThunk(
  "supportCourses/validate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await SupportCoursesService.validateSupportCourse(id);
      return response.data.supportCourse;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error");
    }
  }
);

// Thunk pour rejeter un cours de soutien
export const rejectSupportCourse = createAsyncThunk(
  "supportCourses/reject",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await SupportCoursesService.rejectSupportCourse(
        id,
        reason
      );
      return response.data.supportCourse;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error");
    }
  }
);

// Initial state
const initialState = {
  supportCourses: [],
  supportCourse: null,
  loading: false,
  error: null,
  availableStudentSupportCourses: [],
};

// Slice
const supportCoursesSlice = createSlice({
  name: "supportCourses",
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Get all support courses
      .addCase(getSupportCourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSupportCourses.fulfilled, (state, { payload }) => {
        state.supportCourses = payload;
        state.loading = false;
      })
      .addCase(getSupportCourses.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Get one support course
      .addCase(getSupportCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSupportCourse.fulfilled, (state, { payload }) => {
        state.supportCourse = payload;
        state.loading = false;
      })
      .addCase(getSupportCourse.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Create support course
      .addCase(createSupportCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSupportCourse.fulfilled, (state, { payload }) => {
        state.supportCourses.push(payload);
        state.loading = false;
      })
      .addCase(createSupportCourse.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Update support course
      .addCase(updateSupportCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSupportCourse.fulfilled, (state, { payload }) => {
        const index = state.supportCourses.findIndex(
          (course) => course._id === payload._id
        );
        if (index !== -1) {
          state.supportCourses[index] = payload;
        }
        state.supportCourse = payload;
        state.loading = false;
      })
      .addCase(updateSupportCourse.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Delete support course
      .addCase(deleteSupportCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSupportCourse.fulfilled, (state, { payload }) => {
        state.supportCourses = state.supportCourses.filter(
          (course) => course._id !== payload
        );
        if (state.supportCourse?._id === payload) {
          state.supportCourse = null;
        }
        state.loading = false;
      })
      .addCase(deleteSupportCourse.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Add students
      .addCase(addStudents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addStudents.fulfilled, (state, { payload }) => {
        if (state.supportCourse?._id === payload._id) {
          state.supportCourse = payload;
        }
        const index = state.supportCourses.findIndex(
          (course) => course._id === payload._id
        );
        if (index !== -1) {
          state.supportCourses[index] = payload;
        }
        state.loading = false;
      })
      .addCase(addStudents.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Delete student
      .addCase(deleteStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStudent.fulfilled, (state, { payload }) => {
        if (state.supportCourse?._id === payload.id) {
          state.supportCourse.students = state.supportCourse.students.filter(
            (student) => student.studentId !== payload.studentId
          );
        }
        const index = state.supportCourses.findIndex(
          (course) => course._id === payload.id
        );
        if (index !== -1) {
          state.supportCourses[index].students = state.supportCourses[
            index
          ].students.filter(
            (student) => student.studentId !== payload.studentId
          );
        }
        state.loading = false;
      })
      .addCase(deleteStudent.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // Get available student support courses for support course
      .addCase(getAvailableStudentSupportCourses.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getAvailableStudentSupportCourses.fulfilled,
        (state, { payload }) => {
          state.availableStudentSupportCourses = payload;
          state.loading = false;
        }
      )
      .addCase(
        getAvailableStudentSupportCourses.rejected,
        (state, { payload }) => {
          state.error = payload;
          state.loading = false;
        }
      )
      // Validate support course
      .addCase(validateSupportCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateSupportCourse.fulfilled, (state, { payload }) => {
        const index = state.supportCourses.findIndex(
          (course) => course._id === payload._id
        );
        if (index !== -1) {
          state.supportCourses[index] = payload;
        }
        if (state.supportCourse?._id === payload._id) {
          state.supportCourse = payload;
        }
        state.loading = false;
      })
      .addCase(validateSupportCourse.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Reject support course
      .addCase(rejectSupportCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectSupportCourse.fulfilled, (state, { payload }) => {
        const index = state.supportCourses.findIndex(
          (course) => course._id === payload._id
        );
        if (index !== -1) {
          state.supportCourses[index] = payload;
        }
        if (state.supportCourse?._id === payload._id) {
          state.supportCourse = payload;
        }
        state.loading = false;
      })
      .addCase(rejectSupportCourse.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      });
  },
});

export const { resetState } = supportCoursesSlice.actions;

export default supportCoursesSlice.reducer;
